import { getMCPMonitorGrowthRate } from "@/api/endpoints/searches";
import { useI18n } from "@/contexts/i18nContext";
import { cn } from "@/lib/utils";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { formatDateForApi } from "@/utils";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetStat,
  WidgetTitle,
  WidgetTrend,
} from "@components/ui/widget";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "@components/widgets/utils";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";

export default function TotalPRValueWidget({
  diffInDays,
  searchUuid,
  className,
  filters,
  ...rest
}: WidgetProps & {
  searchUuid: Uuid;
  filters: SearchFilterV4WithDates;
  diffInDays: number;
}) {
  const { abbreviateCurrency, currency } = useI18n();

  // Server state
  const { data, isLoading } = useQuery({
    queryKey: [searchUuid, "insights-reach", "total-pr-value", filters],
    queryFn: async () => {
      return getMCPMonitorGrowthRate(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        compare_start_date: formatDateForApi(
          subDays(new Date(filters.start_date), diffInDays + 1)
        ),
        compare_end_date: formatDateForApi(
          subDays(new Date(filters.end_date), diffInDays + 1)
        ),
        metric: "pr_value",
        currency,
      });
    },
  });

  return (
    <Widget className={cn("h-23.5 gap-0.5", className)} {...rest}>
      <WidgetHeader isLoading={isLoading}>
        <WidgetTitle>
          <Trans>Uppskattat medievärde</Trans>
        </WidgetTitle>
        {data?.ok ? (
          <WidgetStat>{abbreviateCurrency(data.data.value)}</WidgetStat>
        ) : null}
      </WidgetHeader>
      <WidgetContent>
        {data?.ok ? <WidgetTrend value={data.data.change_pct / 100} /> : null}
      </WidgetContent>
    </Widget>
  );
}
